import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ErrorService } from "@/lib/errors/ErrorService";
import { useAuth } from "@/components/AuthProvider";
import { CABINET_MEMBER_ME_KEY } from "@/hooks/useCabinetMemberMe";

export interface NewCabinet {
  id: string;
  cabinet_name: string;
  created_at: string;
  workspace_first_time: boolean;
}

interface NewCabinetsResponse {
  success: boolean;
  data: NewCabinet[];
  count: number;
}

export const useNewCabinets = () => {
  const { user } = useAuth();

  return useQuery<NewCabinet[]>({
    queryKey: ["new-cabinets"],
    enabled: !!user,
    queryFn: async () => {
      try {
        const response = await apiClient.get<NewCabinetsResponse>('/cabinet-members/new-cabinets');
        return response?.data || [];
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'fetch_new_cabinets' }, silent: true });
        return [];
      }
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes — workspace_first_time changes very rarely
  });
};

export const useMarkCabinetsAsVisited = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      await apiClient.patch('/cabinet-members/mark-visited', {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["new-cabinets"] });
      queryClient.invalidateQueries({ queryKey: ["cabinet-member-data"] });
      queryClient.invalidateQueries({ queryKey: ["cabinet-member"] });
      queryClient.invalidateQueries({ queryKey: [CABINET_MEMBER_ME_KEY] });
    },
  });
};
